import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatusIndicator } from '@/components/ui/status-indicator';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { SensitiveData } from '@/components/sensitive-data';
import { KpiCard } from '@/components/kpi-card';
import {
  fetchProjects,
  fetchPortfolioSummary,
  fetchClients,
} from '@/lib/data-service';
import {
  formatCurrency,
  formatPercent,
  healthToVariant,
} from '@/lib/utils';
import type { Project, PortfolioSummary, Client } from '@/types';
import { Briefcase, TrendingUp, DollarSign, Wallet } from 'lucide-react';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { useCompany } from '@/contexts/CompanyContext';

export function PortfolioPage() {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [portfolio, setPortfolio] = useState<PortfolioSummary | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const { handleApiError } = useErrorHandler({ useToast: true });
  const { currentCompany } = useCompany();

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [projectsResult, portfolioData, clientsData] = await Promise.all([
          fetchProjects('portfolio', { page: 1, pageSize: 50 }, { statusId: 2, sortBy: 'health' }),
          fetchPortfolioSummary('portfolio'),
          fetchClients('portfolio'),
        ]);
        const projectsData = Array.isArray(projectsResult) ? projectsResult : (projectsResult?.data ?? []);
        setProjects(projectsData);
        setPortfolio(portfolioData);
        setClients(clientsData);
      } catch (error) {
        handleApiError(error, '/api/portfolio', 'GET');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [handleApiError, currentCompany?.id]);

  const activeProjects= projects.filter(p => p.statusId === 2);

  return (
    <div className="space-y-6">
      <PageHeaderBlock
        title="Portfolio Overview"
        subtitle="Executive view across all projects"
        size={20}
        showActions={false}
      />

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4" data-tour="portfolio-summary-cards">
        <KpiCard
          title="Total Projects"
          value={portfolio?.totalProjects || 0}
          icon={Briefcase}
          description={`${portfolio?.activeProjects || 0} active`}
          accentVariant="info"
        />

        <KpiCard
          title="Portfolio Value"
          value={<SensitiveData>{formatCurrency(portfolio?.portfolioValue || 0)}</SensitiveData>}
          icon={DollarSign}
          description="Total contract value"
          accentVariant="info"
        />

        <KpiCard
          title="Avg Margin"
          value={<SensitiveData>{formatPercent(portfolio?.avgMarginPercent || 0)}</SensitiveData>}
          icon={TrendingUp}
          description={(portfolio?.avgMarginPercent || 0) >= 15 ? 'Above target' : (portfolio?.avgMarginPercent || 0) >= 10 ? 'Near target' : 'Below target'}
          accentVariant={(portfolio?.avgMarginPercent || 0) >= 15 ? 'success' : (portfolio?.avgMarginPercent || 0) >= 10 ? 'warning' : 'error'}
        />

        <KpiCard
          title="Total WIP"
          value={<SensitiveData>{formatCurrency(portfolio?.totalWIP || 0)}</SensitiveData>}
          icon={Wallet}
          description="Unbilled work"
          accentVariant="info"
        />
      </div>

      {/* Health Heat Map */}
      <Card data-tour="portfolio-health-map">
        <CardHeader>
          <CardTitle>Project Health Heat Map</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {activeProjects.map(project => {
              // Get client name - use clientNames if available, otherwise look up first client
              const clientName = project.clientNames || (project.clientIds?.split(',')[0] ? clients.find(c => c.id === Number(project.clientIds?.split(',')[0]))?.name : undefined);
              return (
                <Card
                  key={project.id}
                  className={`cursor-pointer hover:shadow-md transition-shadow ${
                    project.health === 'red'
                      ? 'border-error-300 bg-error-50 dark:bg-error-950/20'
                      : project.health === 'amber'
                      ? 'border-warning-300 bg-warning-50 dark:bg-warning-950/20'
                      : 'border-success-300 bg-success-50 dark:bg-success-950/20'
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <Badge variant={healthToVariant(project.health)} className="gap-1.5">
                        <StatusIndicator variant={project.health === 'green' ? 'success' : project.health === 'amber' ? 'warning' : 'error'} size="sm" />
                        {project.health === 'green' ? 'Healthy' : project.health === 'amber' ? 'At Risk' : 'Critical'}
                      </Badge>
                      <h4 className="font-semibold text-sm line-clamp-2">{project.name}</h4>
                      <p className="text-xs text-muted-foreground">{clientName}</p>
                      <div className="text-xs">
                        <SensitiveData>
                          {formatCurrency(project.budgetRevenue)}
                        </SensitiveData>
                      </div>
                      <div className="text-xs font-medium">
                        {formatPercent(project.completionPercent, 0)} complete
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Status Breakdown */}
      <div className="grid gap-4 md:grid-cols-2" data-tour="portfolio-project-list">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Projects by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {portfolio && Object.entries(portfolio.projectsByStatus).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className="text-sm capitalize">{status.replace('-', ' ')}</span>
                  <Badge variant="outline">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Projects by Health</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {portfolio && Object.entries(portfolio.projectsByHealth).map(([health, count]) => (
                <div key={health} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className={`h-3 w-3 rounded-full ${
                        health === 'green'
                          ? 'bg-success-600'
                          : health === 'amber'
                          ? 'bg-warning-600'
                          : 'bg-error-600'
                      }`}
                    />
                    <span className="text-sm">{health === 'green' ? 'Healthy' : health === 'amber' ? 'At Risk' : 'Critical'}</span>
                  </div>
                  <Badge variant="outline">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
